import type { HarmonyPackageDocument } from '@rhythmic/db/types'
import type { Transaction } from '@tanstack/vue-db'
import { createOptimisticAction } from '@tanstack/vue-db'
import { useRhythmicLogger } from '#imports'

import { getHarmonyDocumentCollection } from '../getCollections'

const logger = useRhythmicLogger('useHarmonyPlanContext:updateDocument')

export interface UpdateHarmonyPlanDocumentPayload {
  name?: string
  image?: string | null
  context?: Record<string, unknown> | string | null
  document?: string | ArrayBuffer | Uint8Array | null
  markdown?: string | null
  publishStatus?: string | null
  ragSummary?: string | null
  ragOneLiner?: string | null
  folderId?: string | null
}

interface UpdateContext {
  workspaceId: string
  planId: string
}

function validateParams(documentId: string, payload: UpdateHarmonyPlanDocumentPayload, context: UpdateContext) {
  if (!documentId) throw new Error('Document identifier is required to update a harmony plan document')

  if (!context.workspaceId) throw new Error('Workspace identifier is required to update a harmony plan document')

  if (!context.planId) throw new Error('Harmony plan identifier is required to update a document')

  if (
    payload.name === undefined &&
    payload.image === undefined &&
    payload.context === undefined &&
    payload.document === undefined &&
    payload.markdown === undefined &&
    payload.publishStatus === undefined &&
    payload.ragSummary === undefined &&
    payload.ragOneLiner === undefined &&
    payload.folderId === undefined
  ) {
    throw new Error('At least one field must be provided to update a harmony plan document')
  }

  return { documentId, planId: context.planId }
}

export function handleUpdateHarmonyPlanDocument(
  documentId: string,
  payload: UpdateHarmonyPlanDocumentPayload,
  context: UpdateContext,
): Transaction {
  const { planId } = validateParams(documentId, payload, context)
  const collection = getHarmonyDocumentCollection(planId)

  const updateTx = createOptimisticAction<{
    documentId: string
    payload: UpdateHarmonyPlanDocumentPayload
  }>({
    onMutate: (mutation) => {
      logger.debug('Optimistically updating harmony plan document', {
        documentId: mutation.documentId,
        payload: mutation.payload,
      })
      collection.update(mutation.documentId, (draft: HarmonyPackageDocument) => {
        if (mutation.payload.name !== undefined) draft.name = mutation.payload.name
        if (mutation.payload.image !== undefined) draft.image = mutation.payload.image
        if (mutation.payload.context !== undefined) draft.context = mutation.payload.context as any
        if (mutation.payload.document !== undefined) draft.document = mutation.payload.document as any
        if (mutation.payload.markdown !== undefined) draft.markdown = mutation.payload.markdown
        if (mutation.payload.publishStatus !== undefined) draft.publish_status = mutation.payload.publishStatus
        if (mutation.payload.ragSummary !== undefined) draft.rag_summary = mutation.payload.ragSummary
        if (mutation.payload.ragOneLiner !== undefined) draft.rag_one_liner = mutation.payload.ragOneLiner
        if (mutation.payload.folderId !== undefined) draft.folder_id = mutation.payload.folderId
        draft.updated_at = new Date()
      })
    },
    mutationFn: async (mutation) => {
      const body: Record<string, unknown> = {}
      if (mutation.payload.name !== undefined) body.name = mutation.payload.name
      if (mutation.payload.image !== undefined) body.image = mutation.payload.image
      if (mutation.payload.context !== undefined) body.context = mutation.payload.context
      if (mutation.payload.document !== undefined) body.document = mutation.payload.document
      if (mutation.payload.markdown !== undefined) body.markdown = mutation.payload.markdown
      if (mutation.payload.publishStatus !== undefined) body.publishStatus = mutation.payload.publishStatus
      if (mutation.payload.ragSummary !== undefined) body.ragSummary = mutation.payload.ragSummary
      if (mutation.payload.ragOneLiner !== undefined) body.ragOneLiner = mutation.payload.ragOneLiner
      if (mutation.payload.folderId !== undefined) body.folder_id = mutation.payload.folderId

      logger.debug('Persisting harmony plan document update via API', {
        documentId: mutation.documentId,
        body,
      })
      const result = await $fetch<{ txid?: string | number }>(`/api/harmony-package-documents/${mutation.documentId}`, {
        method: 'PUT',
        body,
      })

      if (result?.txid && collection.utils.awaitTxId) {
        logger.debug('Awaiting transaction confirmation for document update', {
          txid: result.txid,
        })
        await collection.utils.awaitTxId(Number.parseInt(result.txid.toString()))
      }
    },
  })

  return updateTx({ documentId, payload })
}
